/** @jsx jsx */
import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import { Box, Container, Flex, Grid, Heading, jsx, Text } from 'theme-ui'
import MakerCard from '~/components/Maker/Card'
import { SliderWrapper } from './styles'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'
import Chevron from '~/assets/images/icons/chevron.inline.svg'

function SliderArrow(props) {
  const { className, label, onClick } = props
  return (
    <button className={className} onClick={onClick}>
      <Chevron color="black" height="12" width="18" />
      <VisuallyHidden as="span">{label}</VisuallyHidden>
    </button>
  )
}

const MakerMegaCarouselSection = ({ description, makers, subtitle, title }) => {
  const anchorRef = useRef(null)

  const settings = {
    centerMode: true,
    centerPadding: '0',
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    initialSlide: 0,
    nextArrow: <SliderArrow label="Next" />,
    prevArrow: <SliderArrow label="Previous" />,
    responsive: [
      {
        breakpoint: 1169,
        settings: {
          centerMode: true,
          centerPadding: '96px',
        },
      },
      {
        breakpoint: 1023,
        settings: {
          centerMode: true,
          centerPadding: '48px',
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 831,
        settings: {
          centerMode: true,
          centerPadding: '88px',
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 639,
        settings: {
          arrows: false,
          centerMode: true,
          centerPadding: '28px',
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  }

  const handleAnchorClick = (e) => {
    e.preventDefault()
    anchorRef.current.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const anchorLink = document.querySelector('a[href="#meet-our-makers"]')

    if (anchorLink) {
      // Smooth scroll from schedule button to form anchor
      anchorLink.removeEventListener('click', handleAnchorClick)
      anchorLink.addEventListener('click', handleAnchorClick)
    }
  }, [])

  return (
    <Box
      bg="backgroundMedium"
      sx={{ overflow: 'hidden', position: 'relative' }}
    >
      <Box
        id="meet-our-makers"
        ref={anchorRef}
        sx={{ position: 'absolute', top: '-96px' }}
      />
      <Container
        pt={[7, 9, null, 13]}
        pb={[8, 10, null, 14]}
        sx={{ position: 'relative', zIndex: '1' }}
      >
        <img
          src="/images/backgrounds/maker-blob-left.svg"
          alt=""
          aria-hidden="true"
          sx={{
            bottom: [null, '0'],
            display: ['none', 'block'],
            right: [
              null,
              'calc(100% - 260px)',
              'calc(100% - 370px)',
              'calc(100% - 450px)',
            ],
            pointerEvents: 'none',
            position: 'absolute',
          }}
        />
        <img
          src="/images/backgrounds/maker-blob-right.svg"
          alt=""
          aria-hidden="true"
          sx={{
            bottom: ['-180px', '0', '-70px', '0'],
            left: [
              'calc(100% - 160px)',
              'calc(100% - 180px)',
              'calc(100% - 260px)',
            ],
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: '-1',
          }}
        />
        <Grid
          columns={[1, 12]}
          mb={[6, 7, null, 9]}
          sx={{ rowGap: '0', textAlign: 'center' }}
        >
          <Text
            as="h2"
            variant="subtitle1"
            mb={2}
            sx={{ gridColumn: [null, '1 / -1'] }}
          >
            {title}
          </Text>
          <Heading
            as="h3"
            variant="title2"
            mb={2}
            sx={{ gridColumn: [null, '1 / -1', '2 / 12', '3 / 11'] }}
          >
            {subtitle}
          </Heading>
          {description && (
            <Box
              variant="text.body1"
              sx={{
                gridColumn: [null, '2 / 12', null, '3 / 11'],
                '& p': { m: 0 },
              }}
              dangerouslySetInnerHTML={{
                __html: description.childMarkdownRemark.html,
              }}
            />
          )}
        </Grid>
        <Box
          mx={['-16px', '-24px']}
          sx={{ width: ['calc(100% + 32px)', 'calc(100% + 48px)'] }}
        >
          <SliderWrapper>
            <Slider {...settings}>
              {makers.map((maker, index) => {
                return (
                  <Flex
                    key={`maker-${index}`}
                    px={[1, 3, '12px', 4]}
                    sx={{
                      height: '100%',
                      '&:focus:not(:focus-visible)': { outline: 'none' },
                      '[aria-hidden="true"] & a': {
                        visibility: 'hidden',
                      },
                    }}
                  >
                    <MakerCard maker={maker} />
                  </Flex>
                )
              })}
            </Slider>
          </SliderWrapper>
        </Box>
      </Container>
    </Box>
  )
}

MakerMegaCarouselSection.propTypes = {
  makers: PropTypes.arrayOf(PropTypes.shape({})),
}

export default MakerMegaCarouselSection
